import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { GlobalContext } from '../../../App'
import PercentageGauge from '../../../components/Charts/PercentageGauge'

import CloseIcon from '@mui/icons-material/Close'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import DescriptionIcon from '@mui/icons-material/Description'
import LaunchIcon from '@mui/icons-material/Launch'
import SaveIcon from '@mui/icons-material/Save'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

const PolicyTemplateDrawer = ({ open, policyId, policies, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ policyTitle: '', category: '', archived: false })
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open || !policyId) return
    setDetail(null)
    setLoading(true)
    API.get(`/policies/descriptions/${policyId}/detail`)
      .then((res) => {
        if (res.status !== 200) {
          setToast({ message: 'Could not load the policy template' })
          return
        }
        setDetail(res.data)
        setForm({
          policyTitle: res.data.description?.policyTitle ?? '',
          category: res.data.description?.category ?? '',
          archived: Boolean(res.data.description?.archived),
        })
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false))
  }, [open, policyId])

  const versions = detail?.versions ?? []
  const latestVersion = versions[0]
  const totalActiveStaff = detail?.totalActiveStaff ?? 0
  const latestPercent =
    latestVersion && totalActiveStaff > 0
      ? Math.round((latestVersion.acknowledgedCount / totalActiveStaff) * 100)
      : 0

  const handleSave = () => {
    setSaving(true)
    API.post('/policies/descriptions', { _id: policyId, ...form })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Policy Template Updated' })
          onChanged()
        } else {
          setToast({ message: 'Could not save the policy template' })
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setSaving(false))
  }

  const handleDelete = () => {
    API.delete(`/policies/descriptions/${policyId}`)
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Policy Template Deleted' })
          setConfirmDelete(false)
          onChanged()
          onClose()
        } else {
          setToast({
            message:
              res.data?.error || 'Could not delete the policy template',
          })
        }
      })
      .catch((e) => console.error(e))
  }

  const handleViewVersion = (version) => {
    if (!version?.docLink) {
      setToast({ message: 'This version has no document attached' })
      return
    }
    API.post('/gets3downloadurl', {
      key: version.docLink,
      ACL: 'public-read',
      expires: 3000,
    })
      .then((res) => {
        if (res.status === 200 && res.data) {
          window.open(res.data, '_blank', 'noopener')
        } else {
          setToast({ message: 'Could not open the document' })
        }
      })
      .catch((e) => console.error(e))
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
    >
      <Stack height="100%">
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #0B1F3A 0%, #146C94 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <DescriptionIcon />
              <Box>
                <Typography variant="h6" fontWeight={800} color="white">
                  {detail?.description?.policyTitle ?? 'Policy Template'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {detail?.description?.category && (
                    <Chip
                      label={detail.description.category}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.16)',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {detail?.description?.archived && (
                    <Chip
                      label="Archived"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(244,63,94,0.35)',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {loading && <LinearProgress />}

        <Stack spacing={2} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
          >
            <Stack spacing={1.5}>
              <Typography fontWeight={800}>Template Details</Typography>
              <TextField
                label="Policy Title"
                size="small"
                fullWidth
                autoComplete="off"
                value={form.policyTitle}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, policyTitle: e.target.value }))
                }
              />
              <Autocomplete
                fullWidth
                size="small"
                value={form.category}
                freeSolo
                autoSelect
                disableClearable
                onChange={(e, newValue) =>
                  setForm((prev) => ({ ...prev, category: newValue }))
                }
                options={(policies ?? []).reduce(
                  (accumulator, policy) => {
                    if (!accumulator.includes(policy.category)) {
                      accumulator.push(policy.category)
                    }
                    return accumulator
                  },
                  ['Clinical', 'Operational'],
                )}
                renderInput={(params) => (
                  <TextField label="Category" {...params} />
                )}
              />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Switch
                    checked={form.archived}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, archived: e.target.checked }))
                    }
                  />
                  <Typography variant="body2">Archived</Typography>
                </Stack>
                <Button
                  variant="contained"
                  color="anMainBlue"
                  size="small"
                  startIcon={<SaveIcon />}
                  disabled={saving || loading || !form.policyTitle.trim()}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Archiving hides this policy from the Policy Library and staff
                to-do lists without deleting its history.
              </Typography>
            </Stack>
          </Paper>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<LaunchIcon />}
            onClick={() => navigate(`/policy-library/${policyId}`)}
          >
            View Policy Page
          </Button>

          {!loading && detail && versions.length === 0 && (
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
            >
              <Stack spacing={1.5}>
                <Alert severity="info">
                  There are no policy documents uploaded for this template yet.
                  Open the policy page above to upload the first version.
                </Alert>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Permanently Delete Template
                </Button>
              </Stack>
            </Paper>
          )}

          {versions.length > 0 && (
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
            >
              <Stack spacing={1}>
                <Typography fontWeight={800}>
                  Latest Version Acknowledgement
                </Typography>
                <PercentageGauge
                  percent={latestPercent}
                  color={
                    latestPercent >= 90
                      ? 'success'
                      : latestPercent >= 60
                        ? 'warning'
                        : 'error'
                  }
                  title={`Version ${latestVersion.version ?? '-'} — ${
                    latestVersion.acknowledgedCount
                  } of ${totalActiveStaff} active staff acknowledged`}
                  maxHeight="8rem"
                />
              </Stack>
            </Paper>
          )}

          {versions.length > 0 && (
            <Paper
              elevation={0}
              sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
            >
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography fontWeight={800}>
                  Versions ({versions.length})
                </Typography>
              </Box>
              <Stack divider={<Divider />}>
                {versions.map((version, index) => {
                  const percent =
                    totalActiveStaff > 0
                      ? Math.round(
                          (version.acknowledgedCount / totalActiveStaff) * 100,
                        )
                      : 0
                  // Uploaded after the current version but numbered at or
                  // below it, so staff never see it — usually a mis-numbered
                  // upload rather than a deliberate historical back-fill.
                  const newerThanLatest =
                    index > 0 &&
                    version.createdAt &&
                    latestVersion?.createdAt &&
                    moment(version.createdAt).isAfter(latestVersion.createdAt)
                  return (
                    <Box key={version._id} sx={{ px: 2, py: 1.5 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={700}>
                              Version {version.version ?? '-'}
                            </Typography>
                            {index === 0 && (
                              <Chip label="Latest" size="small" color="success" />
                            )}
                            {newerThanLatest && (
                              <Chip
                                label="Newer upload, not current"
                                size="small"
                                color="warning"
                              />
                            )}
                          </Stack>
                          {newerThanLatest && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              display="block"
                            >
                              Uploaded {moment(version.createdAt).format('DD MMM YYYY')},
                              after the current version, but its version number
                              is lower so staff will not see it. If this should
                              be the current policy, re-upload it with a version
                              higher than {latestVersion?.version ?? '-'}.
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {version.date
                              ? moment(version.date).format('DD MMM YYYY')
                              : 'No date'}
                            {' · '}
                            {version.acknowledgedCount} of {totalActiveStaff}{' '}
                            acknowledged
                          </Typography>
                          {version.versionChanges && (
                            <Typography variant="caption" display="block">
                              {version.versionChanges}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewVersion(version)}
                        >
                          View
                        </Button>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{ mt: 1, height: 6, borderRadius: 99 }}
                        color={
                          percent >= 90
                            ? 'success'
                            : percent >= 60
                              ? 'warning'
                              : 'error'
                        }
                      />
                    </Box>
                  )
                })}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Permanently delete this template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{detail?.description?.policyTitle}" will be permanently deleted.
            This is only possible because no policy documents have been
            uploaded against it. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}

export default PolicyTemplateDrawer
